import BottomBar from './BottomBar';

import React, { useCallback, useEffect, useState } from 'react';
import { FileSearch } from 'lucide-react';
import { calculateTotals, getFormActionButtons, getFormTitle, transformFormData } from '../../utils';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AritoFormV2, FormHeaderActionsV2 } from '@/components/custom/erp';
import { formSchema, FormSchema, initialFormValues } from '../../schema';
import { handleUpdateRowFields } from '../../utils/calc-util';
import { usePriceLookup } from './hooks/usePriceLookup';
import { useAuth } from '@/contexts/auth-context';
import { useFormFieldState } from '../../hooks';

import { usePhieuXuatTraLaiNhaCungCapContext } from '../../context';
import { getDocumentsColumns } from './get-documents-columns';
import { useRepeatRowsHandler } from '@/lib/table-handlers';
import { HoverDropdown } from './Dropdown';
import BasicInfoTab from './BasicInfoTab';
import EInvoiceTab from './EInvoiceTab';
import { useDetailRows } from './hooks';
import HistoryTab from './HistoryTab';

import GetDocumentsInvoice from '@/components/custom/erp/get-documents-v2';
import DetailTab from './DetailTab';
import OtherTab from './OtherTab';
import TaxTab from './TaxTab';

interface FormDialogProps {
  onClose: () => void;
  formMode: 'add' | 'edit' | 'view';
  initialData?: MuaHangRecord;
  onAdd?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  onCopy?: () => void;
  addItem?: (data: MuaHangRecord) => Promise<MuaHangRecord>;
  updateItem?: (uuid: string, data: MuaHangRecord) => Promise<MuaHangRecord>;
  refreshData?: () => Promise<void>;
  patchItem?: (uuid: string, data: MuaHangRecord) => Promise<void>;
  onSubmit?: (data: MuaHangRecord) => void;
}

const FormDialog = ({
  onClose,
  formMode,
  initialData,
  onAdd,
  onEdit,
  onDelete,
  onCopy,
  addItem,
  updateItem,
  refreshData,
  patchItem,
  onSubmit
}: FormDialogProps) => {
  const { entityUnit } = useAuth();
  const [activeTab, setActiveTab] = useState<string>('info');
  const [openDialog, setOpenDialog] = useState('none');

  const { state, actions } = useFormFieldState(initialData || {});

  const handleDetailRowFieldChange = useCallback(
    (row: MuaHangRecord, field: string, value: MuaHangRecord) =>
      handleUpdateRowFields(row, field, value, state.taxModel?.thue_suat),
    [state.taxModel?.thue_suat]
  );

  const {
    rows,
    setRows,
    selectedRowUuid: detailSelectedRowUuid,
    selectedCell: detailSelectedCell,
    handleRowClick: detailHandleRowClick,
    handleCellClick: detailHandleCellClick,
    handleAddRow: detailHandleAddRow,
    handleDeleteRow: detailHandleDeleteRow,
    handleCopyRow: detailHandleCopyRow,
    handlePasteRow: detailHandlePasteRow,
    handleMoveRow: detailHandleMoveRow,
    handleCellValueChange: detailHandleCellValueChange,
    setPriceLookupHandler,
    setNonWritableFields: detailSetNonWritableFields,
    setPasteConfigs: detailSetPasteConfigs,
    isPasting: detailIsPasting
  } = useDetailRows(initialData?.chi_tiet || [], undefined, handleDetailRowFieldChange, state.taxModel?.thue_suat);

  const priceLookupHandler = usePriceLookup();
  useEffect(() => {
    setPriceLookupHandler(priceLookupHandler);
  }, [priceLookupHandler, setPriceLookupHandler]);

  const handleRepeatDetailRows = useRepeatRowsHandler({
    setRows: setRows,
    markDirty: () => setOpenDialog(prev => (prev === 'dirty' ? prev : 'dirty'))
  });

  const totalResults = calculateTotals(rows);
  const { so_luong, thanh_tien, thue, tong_tien } = totalResults;

  useEffect(() => {
    const taxRate = Number(state.taxModel?.thue_suat);
    rows.forEach((row: MuaHangRecord) => {
      if (!row?.uuid) return;
      const tienNt = Number(row.tien_nt) || 0;
      const computedTax = Number.isNaN(taxRate) ? 0 : (tienNt * taxRate) / 100;
      const currentTax = Number(row.thue_nt);
      const normalizedCurrentTax = Number.isNaN(currentTax) ? 0 : currentTax;
      if (Math.abs(normalizedCurrentTax - computedTax) > 1e-6) {
        detailHandleCellValueChange(row.uuid, 'thue_nt', computedTax);
      }
    });
  }, [state.taxModel?.thue_suat, rows, detailHandleCellValueChange]);

  const handleSubmit = async (data: MuaHangRecord) => {
    try {
      const formData = transformFormData(data, state, rows, entityUnit, totalResults);

      if (onSubmit) {
        onSubmit(formData);
        return;
      }

      if (formMode === 'add') {
        await addItem?.(formData);
        if (formData.chi_tiet && Array.isArray(formData.chi_tiet)) {
          for (const chiTiet of formData.chi_tiet) {
            const chiTietHoaDon = rows.find((ct: MuaHangRecord) => ct.line_hd?.uuid === chiTiet.line_hd);
            if (chiTiet.line_hd && chiTietHoaDon) {
              await patchItem?.(chiTiet.line_hd, { sl_cl: chiTietHoaDon.sl_cl - chiTiet.so_luong });
            }
          }
        }
      } else if (formMode === 'edit' && initialData) {
        await updateItem?.(initialData?.uuid, { ...formData });
        if (formData.chi_tiet && Array.isArray(formData.chi_tiet)) {
          for (const chiTiet of formData.chi_tiet) {
            const chiTietPhieu = initialData?.chi_tiet?.find(
              (ct: MuaHangRecord) => ct.line_hd?.uuid === chiTiet.line_hd
            );
            const detailRow = rows.find((ct: MuaHangRecord) => ct.line_hd?.uuid === chiTiet.line_hd);
            if (chiTiet.line_hd)
              await patchItem?.(chiTiet.line_hd, {
                sl_cl: Number(detailRow?.line_hd?.sl_cl) - Number(chiTiet.so_luong) + Number(chiTietPhieu?.so_luong)
              });
          }
        }
      }
      await refreshData?.();
      onClose();
    } catch (error) {
      return;
    }
  };

  const handleClose = () => {
    onClose();
  };

  const handleSearchData = (searchedData: MuaHangRecord) => {
    const { xprocess, customer, selectedRows } = searchedData;

    if (customer) actions.setNhaCungCap(customer);

    const updatedRows = selectedRows.map((row: MuaHangRecord, index: number) => ({
      ...row,
      uuid: `row-${row.uuid}`,
      line: index + 1,
      gia_nt: row.gia_nt0,
      tien_nt: row.tien_nt0,
      tien_nt0: row.tien_nt0,
      so_luong: row.sl_cl,
      sl_cl: row.sl_cl,
      id_hd: row.hoa_don_data,
      thue_nt: 0,
      thue: 0,
      line_hd: {
        uuid: row.uuid,
        line: row.line
      }
    }));

    let mapRows = [];
    if (xprocess === '1') mapRows = [...rows, ...updatedRows];
    else mapRows = updatedRows;

    setRows(mapRows);
  };

  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);
  };

  const title = getFormTitle(formMode, activeTab);
  const actionButtons = getFormActionButtons(formMode, activeTab, {
    onAdd,
    onEdit,
    onDelete,
    onCopy,
    handleClose
  });

  return (
    <>
      <AritoFormV2<FormSchema>
        mode={formMode}
        title={title}
        actionButtons={actionButtons}
        subTitle='Phiếu xuất trả lại nhà cung cấp'
        schema={formSchema}
        initialData={initialData || initialFormValues}
        onSubmit={handleSubmit}
        onClose={onClose}
        headerFields={
          <Tabs value={activeTab} onValueChange={handleTabChange} className='w-full'>
            <div className='flex items-stretch justify-between border-b border-[#d7d7d7] bg-white'>
              <TabsList className='h-8 w-full justify-start rounded-none border-b-0 bg-muted p-0 text-slate-600'>
                <TabsTrigger
                  value='info'
                  className='relative h-8 rounded-none border-b-[3px] border-transparent bg-transparent px-4 py-1 text-[12px] font-semibold text-slate-500 shadow-none ring-0 transition-colors focus-visible:ring-0 data-[state=active]:border-b-main data-[state=active]:bg-main/10 data-[state=active]:text-main data-[state=active]:shadow-none'
                >
                  Thông tin
                </TabsTrigger>
                {formMode === 'view' && (
                  <TabsTrigger
                    value='history'
                    className='relative h-8 rounded-none border-b-[3px] border-transparent bg-transparent px-4 py-1 text-[12px] font-semibold text-slate-500 shadow-none ring-0 transition-colors focus-visible:ring-0 data-[state=active]:border-b-main data-[state=active]:bg-main/10 data-[state=active]:text-main data-[state=active]:shadow-none'
                  >
                    Lịch sử
                  </TabsTrigger>
                )}
              </TabsList>
              <FormHeaderActionsV2
                mode={formMode}
                className='-mr-1'
                source={{
                  label: 'Hóa đơn mua hàng trong nước',
                  onClick: () => setOpenDialog('get-documents')
                }}
                info={<HoverDropdown uuid={initialData?.uuid} ma_ct={initialData?.chung_tu} />}
              />
            </div>
            <TabsContent value='info' className='mt-0 outline-none'>
              <BasicInfoTab formMode={formMode} formState={{ state, actions }} />
            </TabsContent>
            {formMode === 'view' && (
              <TabsContent value='history' className='mt-0 outline-none'>
                <HistoryTab />
              </TabsContent>
            )}
          </Tabs>
        }
        tabs={
          activeTab === 'info' && [
            {
              id: 'details',
              label: 'Chi tiết',
              component: (
                <DetailTab
                  formMode={formMode}
                  rows={rows}
                  selectedRowUuid={detailSelectedRowUuid}
                  selectedCell={detailSelectedCell}
                  onRowClick={detailHandleRowClick}
                  onCellClick={detailHandleCellClick}
                  onAddRow={detailHandleAddRow}
                  onDeleteRow={detailHandleDeleteRow}
                  onCopyRow={detailHandleCopyRow}
                  onPasteRow={detailHandlePasteRow}
                  onMoveRow={detailHandleMoveRow}
                  onCellValueChange={detailHandleCellValueChange}
                  handleUpdateNonWritableFields={detailSetNonWritableFields}
                  handleUpdatePasteConfigs={detailSetPasteConfigs}
                  onRepeatForRows={handleRepeatDetailRows}
                  constraints={state.taiKhoan?.constraints ?? state.nhaCungCap?.account_data?.constraints ?? []}
                  isPasting={detailIsPasting}
                />
              )
            },
            {
              id: 'tax',
              label: 'Thuế',
              component: <TaxTab formMode={formMode} formState={{ state, actions }} />
            },
            {
              id: 'e-invoice',
              label: 'HĐĐT(Không sử dụng)',
              component: <EInvoiceTab formMode={formMode} />
            },
            {
              id: 'other',
              label: 'Khác',
              component: <OtherTab formMode={formMode} />
            }
          ]
        }
        bottomBar={<BottomBar so_luong={so_luong} thanh_tien={thanh_tien} thue={thue} tong_tien={tong_tien} />}
      />
      {openDialog === 'get-documents' && (
        <GetDocumentsInvoice
          onSearch={handleSearchData}
          onClose={() => setOpenDialog('none')}
          chungTuType={'HDMH'}
          detailColumns={getDocumentsColumns()}
        />
      )}
    </>
  );
};

export default FormDialog;
